/**
 * Client HTTP de l'API HomeCycl'Home.
 *
 * Deux comportements notables :
 *
 *   1. `credentials: 'include'` sur chaque requete. La session vit dans un cookie
 *      `httpOnly` : le jeton n'est jamais manipule en JavaScript, donc jamais
 *      exfiltrable par une injection de script.
 *
 *   2. Rafraichissement transparent. Un 401 declenche UN essai de `POST /auth/refresh`
 *      puis rejoue la requete. Les 401 concurrents partagent la meme promesse de
 *      rafraichissement : dix requetes qui expirent ensemble ne produisent qu'un
 *      seul appel, et une seule rotation de jeton.
 */

const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3010/api';

/** Erreur applicative portant le code metier renvoye par l'API. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors?: Array<{ field: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Vrai lorsque l'erreur decoule d'une regle metier et non d'un incident technique. */
  get isBusinessRule(): boolean {
    return this.status === 409 || this.status === 422;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Interne : empeche une boucle de rafraichissement infinie. */
  _retried?: boolean;
}

/**
 * Points d'entree pour lesquels un 401 ne doit PAS declencher de rafraichissement.
 *
 *   - `/auth/refresh` : ce serait une boucle infinie.
 *   - `/auth/login` et `/auth/register` : un 401 y signifie « identifiants refuses »,
 *     pas « session expiree ». Tenter une rotation masquerait l'erreur reelle.
 *   - `/auth/logout` : la session est deja en train d'etre detruite.
 *
 * Tout le reste — `/auth/me` COMPRIS — doit pouvoir se rafraichir.
 *
 * C'est precisement ce qui manquait : la condition excluait tous les chemins commencant
 * par `/auth/`, donc `/auth/me` aussi. Or c'est l'appel que fait le fournisseur de
 * session a chaque chargement de page. Passe le quart d'heure de validite du jeton
 * d'acces, il recevait 401, le front en concluait « visiteur non connecte », et
 * l'utilisateur se retrouvait deconnecte — alors qu'un jeton de rafraichissement
 * valable sept jours dormait dans son navigateur.
 */
const SANS_RAFRAICHISSEMENT = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/logout'];

function peutSeRafraichir(path: string): boolean {
  return !SANS_RAFRAICHISSEMENT.some((exclu) => path.startsWith(exclu));
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  // Un seul rafraichissement a la fois, partage par tous les appels en attente.
  refreshPromise ??= fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, headers, _retried, ...rest } = options;

  // La base est indispensable : `BASE_URL` vaut « /api », un chemin RELATIF, et
  // `new URL('/api/zones')` leve une TypeError. Le defaut est passe inapercu tant que
  // l'URL de l'API etait absolue — puis, le jour ou le front a relaye l'API sur sa
  // propre origine, PLUS AUCUN appel n'est parti : l'exception etait avalee par
  // TanStack Query, qui n'y voyait qu'une requete en echec. Aucune erreur en console,
  // aucune requete reseau, des donnees vides. Le second argument est ignore lorsque le
  // premier est deja absolu : la ligne fonctionne dans les deux cas.
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const response = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Session expiree : on tente une rotation puis on rejoue une seule fois.
  if (response.status === 401 && !_retried && peutSeRafraichir(path)) {
    if (await refreshSession()) {
      return apiFetch<T>(path, { ...options, _retried: true });
    }
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (payload?.code as string) ?? 'UNKNOWN_ERROR',
      (payload?.message as string) ?? 'Une erreur est survenue.',
      payload?.errors as Array<{ field: string; message: string }> | undefined,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    apiFetch<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

/**
 * Televersement direct vers le stockage objet, sans passer par l'API.
 * L'URL pre-signee porte deja le type MIME et la taille : le navigateur envoie le
 * fichier brut, sans en-tete d'authentification.
 */
export async function uploadToStorage(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  if (!response.ok) {
    throw new Error(`Le televersement de « ${file.name} » a echoue (${response.status}).`);
  }
}
